import _ from 'lodash';
import { BODY_PARTS, MEASUREMENT_TECHNIQUE, TRACKING_TYPES } from './guideline';
import { isBoneLesion } from './isBoneLesion';

/**
 * @param {Object[]} timepoints
 * @param {Object[]} ROIContours
 * @param {String} criteria.measurementTechnique
 * @param {Number} criteria.measurableLongAxis
 * @param {Number} criteria.measurableShortAxis
 * @param {Number} criteria.measurableVolume
 * @param {Number} criteria.maxNumberOfTargets
 * @param {Number} criteria.maxNumberOfTargetsPerOrgan
 */
function getClassifiedROIContours(
  timepoints,
  ROIContours,
  criteria,
  forceUpdate = true
) {
  /**
   * classified: ['target', 'non-target', 'new', 'not-included']
   * unClassified: ['none', 'isolated']
   */
  const unClassifiedROIContours = [];
  const classifiedROIContours = [];
  _.cloneDeep(ROIContours).forEach(roi => {
    if (
      forceUpdate ||
      !roi.trackingType ||
      [TRACKING_TYPES.NONE, TRACKING_TYPES.ISOLATED].includes(roi.trackingType)
    ) {
      unClassifiedROIContours.push(roi);
    } else {
      classifiedROIContours.push(roi);
    }
  });
  const classifiedROINumbers = classifiedROIContours.map(roi => roi.ROINumber);

  /** define baseline lesions */
  const baselineLesions =
    timepoints[0]?.Lesions?.filter(roi => roi.maxAreaValue > 0) || [];

  /** find measurable lesions */
  const _measurableLesions = [];
  switch (criteria.measurementTechnique) {
    case MEASUREMENT_TECHNIQUE.UNIDIMENSIONAL:
      baselineLesions.sort(
        (a, b) => Number(b.maxDiameterValue) - Number(a.maxDiameterValue)
      );
      _measurableLesions.push(
        ...baselineLesions.filter(roi => {
          if (roi.bodyPart === BODY_PARTS.LYMPHNODE) {
            return (
              roi.maxShortAxisValue >= criteria.measurableShortAxisOfLymphNode
            );
          }
          return roi.maxLongAxisValue >= criteria.measurableLongAxis;
        })
      );
      break;
    case MEASUREMENT_TECHNIQUE.BIDIMENSIONAL:
      baselineLesions.sort(
        (a, b) => Number(b.maxDiameterValue) - Number(a.maxDiameterValue)
      );
      _measurableLesions.push(
        ...baselineLesions.filter(roi => {
          if (roi.bodyPart === BODY_PARTS.LYMPHNODE) {
            return (
              roi.maxShortAxisValue >= criteria.measurableShortAxisOfLymphNode
            );
          }
          return (
            roi.maxLongAxisValue >= criteria.measurableLongAxis &&
            roi.maxShortAxisValue >= criteria.measurableShortAxis
          );
        })
      );
      break;
    case MEASUREMENT_TECHNIQUE.VOLUMETRIC:
      baselineLesions.sort((a, b) => Number(b.volume) - Number(a.volume));
      _measurableLesions.push(
        ...baselineLesions.filter(
          roi => roi.volume >= criteria.measurableVolume
        )
      );
      break;
    default:
  }
  const measurableLesions = prioritizeIsolatedLesions(_measurableLesions);
  const measurableLesionNumbers = measurableLesions.map(roi => roi.ROINumber);

  /** find target lesions */
  let counts = 0;
  let countsPerOrgan = {};
  classifiedROIContours.forEach(item => {
    if (item.trackingType === 'target') {
      counts++;
      const organ = item.bodyPart;
      countsPerOrgan[organ] = (countsPerOrgan[organ] || 0) + 1;
    }
  });
  const baselineLesionNumbers = baselineLesions.map(roi => roi.ROINumber);
  const targetLesionNumbers = [];
  measurableLesions.forEach(roi => {
    if (classifiedROINumbers.includes(roi.ROINumber)) {
      return;
    }
    if (!countsPerOrgan[roi.bodyPart]) countsPerOrgan[roi.bodyPart] = 0;
    if (
      counts >= criteria.maxNumberOfTargets ||
      countsPerOrgan[roi.bodyPart] >= criteria.maxNumberOfTargetsPerOrgan
    ) {
      return;
    }
    if (
      baselineLesionNumbers.includes(roi.ROINumber) &&
      [
        BODY_PARTS.PLEURA,
        BODY_PARTS.PLEURALEFFUSION,
        // BODY_PARTS.OTHERS,
      ].includes(roi.bodyPart)
    ) {
      return;
    }
    targetLesionNumbers.push(roi.ROINumber);
    counts += 1;
    countsPerOrgan[roi.bodyPart] += 1;
  });
  unClassifiedROIContours.forEach(roi => {
    if (targetLesionNumbers.includes(roi.ROINumber)) {
      roi.trackingType = TRACKING_TYPES.TARGET;
    } else if (baselineLesionNumbers.includes(roi.ROINumber)) {
      roi.trackingType = TRACKING_TYPES.NONTARGET;
    } else {
      if (criteria.guidelineId === 'iRECIST') {
        const newLesionTargetNumbers = getNewLesionTargetNumbers(
          timepoints,
          unClassifiedROIContours,
          criteria
        );
        const isTarget = newLesionTargetNumbers.includes(roi.ROINumber);
        roi.trackingType = isTarget
          ? TRACKING_TYPES.NEW_TARGET
          : TRACKING_TYPES.NEW_NONTARGET;
      } else {
        roi.trackingType = TRACKING_TYPES.NEW;
      }
    }
  });

  /** exclude others */
  unClassifiedROIContours.forEach(roi => {
    if (
      baselineLesionNumbers.includes(roi.ROINumber) &&
      [
        BODY_PARTS.PLEURA,
        BODY_PARTS.PLEURALEFFUSION,
        // BODY_PARTS.OTHERS,
      ].includes(roi.bodyPart)
    ) {
      roi.trackingType = TRACKING_TYPES.NOTINCLUDED;
    }
  });

  /** sort by roi number and label measurability */
  const allROIContours = [...classifiedROIContours, ...unClassifiedROIContours];
  allROIContours.sort((a, b) => Number(a.ROINumber) - Number(b.ROINumber));
  allROIContours.forEach(roi => {
    roi.measurable = measurableLesionNumbers.includes(roi.ROINumber);
  });

  // Bone lesions are non-measurable in all non-RECIP guidelines
  // (RECIST 1.1, mRECIST, iRECIST, RANO-BM, RANO-BM 5mm).
  // trackingType is NOT changed — bone ROIs remain target / non-target.
  // RECIP 1.0 uses getRecipClassifiedROIContours and never calls this function.
  allROIContours.forEach(roi => {
    if (isBoneLesion(roi)) {
      roi.measurable = false;
    }
  });

  return allROIContours;
}

function getNewLesionTargetNumbers(timepoints, rois, criteria) {
  if (criteria.guidelineId !== 'iRECIST') return [];
  let newCounts = 0;
  let newCountsPerOrgan = {};
  const baselineLesions =
    timepoints[0]?.Lesions?.filter(roi => roi.maxAreaValue > 0) || [];
  const baselineLesionNumbers = baselineLesions.map(roi => roi.ROINumber);
  const newLesionTargetNumbers = timepoints.reduce((numbers, t, idx) => {
    if (idx === 0) return numbers;
    const lesions = t.Lesions.filter(
      l =>
        !baselineLesionNumbers.includes(l.ROINumber) &&
        Number(l.maxDiameterValue) >= criteria.measurableLongAxisOfNewLesion
    );
    lesions.sort(
      (a, b) => Number(b.maxDiameterValue) - Number(a.maxDiameterValue)
    );
    for (const l of lesions) {
      if (numbers.includes(l.ROINumber)) continue;
      const roi = rois.find(roi => roi.ROINumber === l.ROINumber);
      if (!roi) continue;
      const isMeasurable = ![
        BODY_PARTS.BONE,
        BODY_PARTS.PLEURALEFFUSION,
      ].includes(roi.bodyPart);
      if (!isMeasurable) continue;
      if (!newCountsPerOrgan[roi.bodyPart]) {
        newCountsPerOrgan[roi.bodyPart] = 0;
      }
      if (
        newCounts >= criteria.maxNumberOfTargets ||
        newCountsPerOrgan[roi.bodyPart] >= criteria.maxNumberOfTargetsPerOrgan
      ) {
        continue;
      }
      numbers.push(roi.ROINumber);
      newCounts += 1;
      newCountsPerOrgan[roi.bodyPart] += 1;
    }
    return numbers;
  }, []);
  return newLesionTargetNumbers;
}

function prioritizeIsolatedLesions(measurableLesions) {
  const isolated = [];
  const nonIsolated = [];
  measurableLesions.forEach(roi => {
    if (roi.trackingType === TRACKING_TYPES.ISOLATED) {
      isolated.push(roi);
    } else {
      nonIsolated.push(roi);
    }
  });
  return [...isolated, ...nonIsolated];
}

export default getClassifiedROIContours;
